'use client';
import Image from 'next/image';
import React from 'react';
import { useState, useEffect } from 'react';

 export default function OurSimpleSection({ heading, steps, frameImg, eclipseImg , title, highlight}) {
   const [openIndex, setOpenIndex] = useState(0);
   const [hoveredIndex, setHoveredIndex] = useState(null);
   const [isVisible, setIsVisible] = useState(false);
   const [circleSize, setCircleSize] = useState({ width: 600, height: 300 });
 
   useEffect(() => {
     setIsVisible(true);
   }, []);
 
   // Auto-rotation functionality like UIDesignProcess
   useEffect(() => {
     const interval = setInterval(() => {
       setOpenIndex((prev) => (prev + 1) % steps.length);
     }, 3000);
     return () => clearInterval(interval);
   }, []);
 
   // Responsive circle size
   useEffect(() => {
     const handleResize = () => {
       if (window.innerWidth < 640) setCircleSize({ width: 200, height: 100 });
       else if (window.innerWidth < 768) setCircleSize({ width: 250, height: 125 });
       else if (window.innerWidth < 1024) setCircleSize({ width: 350, height: 175 });
       else setCircleSize({ width: 500, height: 250 });
     };
     handleResize();
     window.addEventListener('resize', handleResize);
     return () => window.removeEventListener('resize', handleResize);
   }, []);
 
   const toggleContent = (index) => {
     setOpenIndex(openIndex === index ? -1 : index);
   };
 
   const handleMouseEnter = (index) => {
     setHoveredIndex(index);
     setOpenIndex(index);
   };
 
   const handleMouseLeave = () => {
     setHoveredIndex(null);
   };
 
   return (
     <section className="mx-auto max-w-[1750px] bg-[#EDEDED] py-6 px-4 sm:py-8 sm:px-6 md:py-12 md:px-8 lg:py-16 lg:px-12 xl:py-20 xl:px-20">
      
         {/* Title Section */}
         <div className="flex w-full justify-center text-center">
           <h2 className="mb-6 text-center text-xl font-bold leading-snug text-[#172554] sm:text-2xl md:text-3xl lg:text-4xl">
             {title}{' '}
             <span className="text-[#172554]">
               {highlight}
             </span>
           </h2>
         </div>
 
         {/* Accordion Section */}
         
         <div className="w-full flex flex-col lg:flex-row gap-8 lg:gap-12">
             <div className="flex flex-col w-full lg:w-1/2">
               <div className="border-t-2 border-[#5A9BEB] w-full transition-all duration-500" />
               {steps.map((step, index) => (
                 <div 
                   key={step.id} 
                   className={`border-b-2 border-[#5A9BEB] p-3 sm:p-4 lg:p-5 w-full transition-all duration-500 transform ${
                     isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                   }`}
                   style={{ transitionDelay: `${index * 100}ms` }}
                 >
                   <div
                     className="flex items-center justify-between cursor-pointer group transition-all duration-300 hover:bg-white/30 rounded-lg p-2 -m-2"
                     onClick={() => toggleContent(index)}
                     onMouseEnter={() => handleMouseEnter(index)}
                     onMouseLeave={handleMouseLeave}
                   >
                     <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                       <Image
                         src={`/custom-web-development-services/s${index + 1}.png`}
                         alt={step.title}
                         width={40}
                         height={40}
                         className={`h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 object-contain flex-shrink-0 transition-all duration-300 ${
                           openIndex === index ? 'scale-110' : 'scale-100'
                         }`}
                       />
                       <h2 className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-bold text-blue-600 leading-tight transition-all duration-300 group-hover:text-blue-700">
                         {step.title}
                       </h2>
                     </div>
                     <svg
                       xmlns="http://www.w3.org/2000/svg"
                       className={`h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-gray-600 transition-all duration-300 flex-shrink-0 group-hover:text-blue-600 ${
                         openIndex === index ? 'rotate-180 scale-110' : 'rotate-0 scale-100'
                       }`}
                       fill="none"
                       viewBox="0 0 24 24"
                       stroke="currentColor"
                     >
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                     </svg>
                   </div>
 
                   <div
                     className={`mt-2 text-xs sm:text-sm md:text-base font-medium text-gray-500 transition-all duration-200 ${
                       openIndex === index
                         ? 'h-auto opacity-100'
                         : 'h-0 overflow-hidden opacity-0'
                     }`}
                   >
                     <p className="leading-relaxed">{step.content}</p>
                   </div>
                 </div>
               ))}
             </div>
         
 
           {/* Right - Circular Interactive Elements */}
           <div className="flex w-full items-center justify-center lg:w-1/2 mt-8 lg:mt-0">
             <div className="flex items-center justify-center">
 
               <div
                 className="relative flex items-center justify-center"
                 style={{ width: circleSize.width, height: circleSize.height }}
               >
                 <Image
                   src="/custom-web-development-services/c.png"
                   alt="Background Circle"
                   fill
                   className="object-contain"
                 />
                 {steps.map((step, index) => {
                   const angle = (index / steps.length) * 2 * Math.PI
                   const x = (circleSize.width / 2) * Math.cos(angle)
                   const y = (circleSize.height / 2) * Math.sin(angle)
                   const isActive = openIndex === index
                   return (
                     <div
                       key={index}
                       onClick={() => toggleContent(index)}
                       onMouseEnter={() => handleMouseEnter(index)}
                       onMouseLeave={handleMouseLeave}
                       className={`absolute cursor-pointer transition-all duration-300 ${
                         isActive
                           ? 'scale-125 rounded-full bg-[#056fe8] p-1 sm:p-2 shadow-lg'
                           : ''
                       }`}
                       style={{
                         left: `calc(50% + ${x}px)`,
                         top: `calc(50% + ${y}px)`,
                         transform: 'translate(-50%, -50%)',
                       }}
                     >
                       <div className="h-[30px] w-[30px] sm:h-[40px] sm:w-[40px] md:h-[50px] md:w-[50px] lg:h-[60px] lg:w-[60px] xl:h-[70px] xl:w-[70px] rounded-full">
                         <Image
                           src={`/custom-web-development-services/s${index + 1}.png`}
                           alt={step.title}
                           width={70}
                           height={70}
                           className="rounded-full object-contain transition duration-300"
                         />
                       </div>
                     </div>
                   )
                 })}
               </div>
             </div>
          </div>
         </div>
         
     </section>
   );
 }
 